import { useState } from "react";
import { Plus, Pencil, Trash2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { update } from "../lib/vault";
import { accountValue, fetchPriceFor, holdingDescription, holdingValue } from "../lib/prices";
import { currency } from "../lib/api";
import Sheet from "./Sheet";
import HoldingForm from "./HoldingForm";

// Your accounts (ISA, LISA, wallets…) and what's in each, valued live.
export default function Accounts({ wealth }) {
  const { accounts, quotes } = wealth;
  // { accountId, holding? } while the holding sheet is open
  const [editing, setEditing] = useState(null);
  const [newAccount, setNewAccount] = useState(null);

  const saveHolding = async (holding) => {
    const { accountId } = editing;
    await update((s) => {
      const acc = s.accounts.find((a) => a.id === accountId);
      const i = acc.holdings.findIndex((x) => x.id === holding.id);
      if (i >= 0) acc.holdings[i] = holding;
      else acc.holdings.push(holding);
    });
    setEditing(null);
    if (holding.kind !== "cash" && navigator.onLine) {
      const q = await fetchPriceFor(holding).catch(() => null);
      if (!q) toast.error("Saved, but couldn't fetch a price for it yet");
    }
  };

  const removeHolding = async () => {
    const { accountId, holding } = editing;
    await update((s) => {
      const acc = s.accounts.find((a) => a.id === accountId);
      acc.holdings = acc.holdings.filter((x) => x.id !== holding.id);
    });
    setEditing(null);
  };

  const saveAccount = async (e) => {
    e.preventDefault();
    const name = newAccount.name.trim();
    if (!name) return;
    await update((s) => {
      if (newAccount.id) s.accounts.find((a) => a.id === newAccount.id).name = name;
      else s.accounts.push({ id: crypto.randomUUID(), name, holdings: [] });
    });
    setNewAccount(null);
  };

  const removeAccount = async (acc) => {
    if (!window.confirm(`Delete "${acc.name}" and everything in it?`)) return;
    await update((s) => {
      s.accounts = s.accounts.filter((a) => a.id !== acc.id);
    });
  };

  return (
    <div className="nest-card p-5 md:p-6" data-testid="accounts-card">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-widest text-nest-muted">Live</div>
          <h3 className="font-heading text-xl mt-1">Your accounts</h3>
        </div>
        <button
          onClick={() => setNewAccount({ name: "" })}
          className="nest-btn-primary flex items-center gap-1 text-sm"
          data-testid="add-account-btn"
        >
          <Plus size={14} /> Account
        </button>
      </div>

      {accounts.length === 0 && (
        <div className="text-sm text-nest-faint py-6 text-center">
          Add an account (e.g. "Trading212 ISA") and what's in it.
        </div>
      )}

      <div className="space-y-4">
        {accounts.map((acc) => {
          const { value, missing } = accountValue(acc, quotes);
          return (
            <div key={acc.id} className="border border-nest-line rounded-2xl" data-testid={`account-${acc.name}`}>
              <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3">
                <div className="min-w-0">
                  <div className="font-heading text-lg leading-tight break-words">{acc.name}</div>
                  <div className="font-heading text-xl tabular text-nest-good mt-1">{currency(value)}</div>
                  {missing > 0 && (
                    <div className="text-[11px] text-nest-warn">
                      {missing} holding{missing > 1 ? "s" : ""} waiting for a price
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0 pt-1">
                  <button
                    onClick={() => setNewAccount({ id: acc.id, name: acc.name })}
                    className="text-nest-faint hover:text-nest-brand"
                    aria-label={`Rename ${acc.name}`}
                  >
                    <Pencil size={14} strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={() => removeAccount(acc)}
                    className="text-nest-faint hover:text-nest-warn"
                    aria-label={`Delete ${acc.name}`}
                  >
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              <div>
                {acc.holdings.map((h) => {
                  const { value: v } = holdingValue(h, quotes);
                  return (
                    <button
                      key={h.id}
                      onClick={() => setEditing({ accountId: acc.id, holding: h })}
                      className="w-full flex items-center justify-between gap-3 px-4 py-2.5 border-t border-nest-line text-left hover:bg-nest-surface-1"
                      data-testid={`holding-${h.name}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm truncate">{h.name}</div>
                        <div className="text-[11px] text-nest-muted tabular">{holdingDescription(h)}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-sm tabular">{v == null ? "—" : currency(v)}</span>
                        <ChevronRight size={14} className="text-nest-faint/60" />
                      </div>
                    </button>
                  );
                })}
                <button
                  onClick={() => setEditing({ accountId: acc.id })}
                  className="w-full text-left px-4 py-2.5 border-t border-nest-line text-sm text-nest-brand flex items-center gap-1"
                  data-testid={`add-holding-${acc.name}`}
                >
                  <Plus size={14} /> Add holding
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <Sheet
          title={editing.holding ? "Edit holding" : "Add holding"}
          onClose={() => setEditing(null)}
          testId="holding-sheet"
        >
          <HoldingForm
            initial={editing.holding}
            onSave={saveHolding}
            onDelete={editing.holding ? removeHolding : undefined}
          />
        </Sheet>
      )}

      {newAccount && (
        <Sheet
          title={newAccount.id ? "Rename account" : "New account"}
          onClose={() => setNewAccount(null)}
          testId="account-sheet"
        >
          <form onSubmit={saveAccount} className="space-y-4">
            <input
              autoFocus
              className="nest-input w-full"
              placeholder="e.g. Trading212 ISA"
              value={newAccount.name}
              onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
              data-testid="account-name-input"
            />
            <button type="submit" className="nest-btn-primary w-full py-3" data-testid="account-save-btn">
              Save
            </button>
          </form>
        </Sheet>
      )}
    </div>
  );
}
